import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Megaphone, Sparkles, Inbox, Send } from "lucide-react";
import { Pill, Btn, PitchEditor, EmailCoverageSection } from "@/pages/MarketingDashboard";

export default function AdminOutreach() {
  const [busy, setBusy] = useState(null);
  const [leads, setLeads] = useState([]);
  const [outreachStats, setOutreachStats] = useState(null);
  const [pitchingId, setPitchingId] = useState(null);

  const refresh = () => {
    api.get("/outreach/leads").then(({ data }) => setLeads(data)).catch(() => {});
    api.get("/outreach/stats").then(({ data }) => setOutreachStats(data)).catch(() => {});
  };
  useEffect(() => { refresh(); }, []);

  const generatePitch = async (leadId) => {
    setPitchingId(leadId);
    try {
      const { data } = await api.post(`/outreach/leads/${leadId}/generate-pitch`);
      setLeads((ls) => ls.map((l) => l.id === leadId ? { ...l, pitch_text: data.pitch, pitch_generated_at: new Date().toISOString() } : l));
      toast.success("AI pitch drafted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Pitch generation failed");
    } finally { setPitchingId(null); }
  };

  const markContacted = async (leadId) => {
    try {
      await api.patch(`/outreach/leads/${leadId}`, { status: "contacted" });
      setLeads((ls) => ls.map((l) => l.id === leadId ? { ...l, status: "contacted", contacted_at: new Date().toISOString() } : l));
      api.get("/outreach/stats").then(({ data }) => setOutreachStats(data));
      toast.success("Marked as contacted");
    } catch (e) { toast.error("Update failed"); }
  };

  const optOutLead = async (l) => {
    const email = l.contact_email || l.pitch_recipient;
    if (!email) return;
    if (!window.confirm(`Opt ${email} (${l.program_name}) out of ALL CoachConnect emails? Use this when someone replies STOP.`)) return;
    try {
      await api.post("/admin/optout", { email });
      setLeads((ls) => ls.map((x) => x.id === l.id ? { ...x, opted_out: true } : x));
      toast.success(`${email} opted out — no more emails, ever`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Opt-out failed");
    }
  };

  const copyPitch = (text) => {
    navigator.clipboard.writeText(text || "");
    toast.success("Pitch copied — paste into email/LinkedIn");
  };

  const run = async (label, url, opts = {}) => {
    setBusy(label);
    try {
      const { data } = await api.post(url, opts.body || undefined);
      toast.success(`${label}: ${JSON.stringify(data).slice(0, 120)}…`);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || `${label} failed`);
    } finally { setBusy(null); }
  };

  return (
    <div className="text-white">
      <div className="px-4 md:px-8 lg:px-12 py-8 max-w-6xl" data-testid="admin-outreach-page">
        <div className="flex items-center gap-2 text-volt text-xs tracking-widest"><Megaphone size={14} /> ADMIN HQ · OUTREACH</div>
        <h1 className="heading text-4xl md:text-5xl mt-2">OUTREACH</h1>
        <p className="text-neutral-400 mt-2 text-sm max-w-2xl">
          Scraped vacancies → AI-drafted pitches → your approval → send. Drafts land in the APPROVALS queue before anything goes out.
        </p>

        {/* "We pre-built your listing" — Bulk-draft + send campaign */}
        <div className="mt-8 border border-volt/40 bg-appcard p-5">
          <div className="text-xs tracking-widest text-volt" data-testid="prefill-campaign-section">PRE-FILL CAMPAIGN · "WE BUILT YOUR LISTING, JUST LAUNCH"</div>
          <p className="text-xs text-neutral-400 mt-2 leading-relaxed">
            Turn scraped vacancies into magic-link pitches. Claude reads each lead (school + role + faculty info) and drafts a personalized "we pre-built your listing on CoachConnect AI, click to review &amp; launch" email. AD lands on a public page with their listing pre-filled → 1-click claim → Stripe checkout.
          </p>
          <div className="mt-4 flex flex-wrap gap-3 items-center">
            <Btn data-testid="bulk-draft-prefill-20" onClick={() => run("Bulk Draft (20 newest)", "/outreach/bulk-draft-prefill", { body: { limit: 20 } })} busy={busy === "Bulk Draft (20 newest)"}>
              Bulk-draft pitches (20 newest leads)
            </Btn>
            <Btn data-testid="bulk-draft-prefill-50" onClick={() => run("Bulk Draft (50 newest)", "/outreach/bulk-draft-prefill", { body: { limit: 50 } })} busy={busy === "Bulk Draft (50 newest)"}>
              Bulk-draft pitches (50 newest leads)
            </Btn>
          </div>
          <p className="text-[10px] text-neutral-600 mt-3">Drafts go into each lead's <code>pitch_text</code> field. Review individual drafts in the OUTREACH LEADS section below, then click SEND to fire. Each draft uses Claude Sonnet 4.5 (~$0.01/lead).</p>
        </div>

        {/* Bulk-send pitch drip */}
        <div className="mt-6 border border-blaze/40 bg-appcard p-5" data-testid="pitch-drip-panel">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-xs tracking-widest text-blaze">BULK SEND · VACANCY PITCH DRIP</div>
            <button
              data-testid="pitch-drip-toggle-btn"
              disabled={busy === "Pitch Drip"}
              onClick={() => run("Pitch Drip", `/admin/pitch-drip/${outreachStats?.pitch_drip?.enabled ? "pause" : "start"}`)}
              className={`min-h-[44px] px-5 heading text-sm tracking-widest transition-colors ${
                outreachStats?.pitch_drip?.enabled
                  ? "bg-blaze text-white hover:opacity-90"
                  : "border border-volt text-volt hover:bg-volt hover:text-white"
              }`}
            >
              {busy === "Pitch Drip" ? "WORKING…" : outreachStats?.pitch_drip?.enabled ? "PAUSE DRIP" : "START DRIP"}
            </button>
          </div>
          <p className="text-xs text-neutral-400 mt-2 leading-relaxed">
            When ON, ready pitches (draft + contact email) auto-send daily — newest postings first, capped at{" "}
            {outreachStats?.pitch_drip?.daily_cap ?? 25}/day. Sent leads become CONTACTED and get one automatic
            follow-up after 7 days of silence. Unsubscribes, bounces, and sport mismatches are skipped.
          </p>
          {outreachStats?.pitch_drip && (
            <div className="mt-3 flex gap-3 text-xs flex-wrap">
              <Pill label="Status" value={outreachStats.pitch_drip.enabled ? "ON" : "OFF"} accent={outreachStats.pitch_drip.enabled ? "volt" : "blaze"} />
              <Pill label="Ready to send" value={outreachStats.pitch_drip.ready} accent="volt" />
              <Pill label="Sent today" value={outreachStats.pitch_drip.sent_today} />
              <Pill label="Daily cap" value={outreachStats.pitch_drip.daily_cap} />
            </div>
          )}
        </div>

        {/* Outreach leads */}
        <div className="mt-6 border border-neutral-800 bg-appcard p-5" data-testid="outreach-leads-panel">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-volt text-xs tracking-widest"><Inbox size={14} /> OUTBOUND OUTREACH · NEW VACANCIES TO PITCH</div>
            {outreachStats && (
              <div className="flex gap-3 text-xs">
                <Pill label="New" value={outreachStats.new} accent="blaze" />
                <Pill label="Contacted" value={outreachStats.contacted} />
                <Pill label="Qualified" value={outreachStats.qualified} accent="volt" />
                <Pill label="Converted" value={outreachStats.converted} accent="volt" />
              </div>
            )}
          </div>
          <p className="text-xs text-neutral-500 mt-1">
            These are <span className="text-neutral-300">scraped vacancies</span>, not outgoing emails — nothing sends from here. Draft a pitch and it lands in the <a href="/ad/marketing/emails" className="text-volt hover:underline">Email Approval Queue</a>, where every email gets its verification badges and your explicit approval before sending.
          </p>
          <div className="mt-4 space-y-2 max-h-[500px] overflow-y-auto" data-testid="outreach-leads-list">
            {leads.length === 0 && <div className="text-neutral-500 text-sm">No leads yet. Run a scraper on the SOURCES &amp; DATA page to seed.</div>}
            {leads.slice(0, 25).map((l) => (
              <div key={l.id} data-testid={`outreach-lead-${l.id}`} className="border border-neutral-800 bg-appbg p-3">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="text-xs tracking-widest text-neutral-500">
                      <span className={l.status === "new" ? "text-blaze" : l.status === "contacted" ? "text-volt" : "text-neutral-400"}>
                        {(l.status || "new").toUpperCase()}
                      </span>
                      {" · "}{l.level || "—"}{l.division ? ` ${l.division}` : ""} · {l.gender || ""}
                    </div>
                    <div className="heading text-lg mt-1 truncate flex items-center gap-2">
                      <span className="truncate">{l.program_name}</span>
                      {l.sport_verified && (
                        <span className="text-[9px] tracking-widest px-1.5 py-0.5 border border-volt/60 text-volt shrink-0" data-testid={`lead-verified-${l.id}`}>✓ SPORT VERIFIED</span>
                      )}
                      {l.sport_mismatch && (
                        <span className="text-[9px] tracking-widest px-1.5 py-0.5 border border-red-600 text-red-500 shrink-0" data-testid={`lead-mismatch-${l.id}`}>NOT LACROSSE</span>
                      )}
                      {l.opted_out && (
                        <span className="text-[9px] tracking-widest px-1.5 py-0.5 border border-red-600 text-red-500 shrink-0" data-testid={`lead-opted-out-${l.id}`}>OPTED OUT</span>
                      )}
                    </div>
                    <div className="text-sm text-neutral-300 truncate">{l.title}</div>
                    {l.location && <div className="text-xs text-neutral-500 mt-0.5">{l.location}</div>}
                  </div>
                  <div className="flex gap-2 shrink-0 flex-wrap">
                    {!l.pitch_text && (
                      <button
                        data-testid={`gen-pitch-${l.id}`}
                        onClick={() => generatePitch(l.id)}
                        disabled={pitchingId === l.id}
                        className="min-h-[40px] px-3 border border-volt text-volt text-xs tracking-widest hover:bg-volt hover:text-white transition-colors disabled:opacity-50 flex items-center gap-1.5"
                      >
                        <Sparkles size={12} /> {pitchingId === l.id ? "DRAFTING…" : "DRAFT AI PITCH"}
                      </button>
                    )}
                    {l.pitch_text && (
                      <button
                        data-testid={`copy-pitch-${l.id}`}
                        onClick={() => copyPitch(l.pitch_text)}
                        className="min-h-[40px] px-3 border border-neutral-700 text-xs tracking-widest hover:border-volt hover:text-volt flex items-center gap-1.5"
                      >
                        <Send size={12} /> COPY PITCH
                      </button>
                    )}
                    {l.status === "new" && (
                      <button
                        data-testid={`mark-contacted-${l.id}`}
                        onClick={() => markContacted(l.id)}
                        className="min-h-[40px] px-3 bg-blaze text-white text-xs tracking-widest hover:glow-blaze transition-all"
                      >
                        MARK CONTACTED
                      </button>
                    )}
                    {(l.contact_email || l.pitch_recipient) && !l.opted_out && (
                      <button
                        data-testid={`optout-lead-${l.id}`}
                        onClick={() => optOutLead(l)}
                        title="Opt this contact out of ALL emails (STOP reply)"
                        className="min-h-[40px] px-3 border border-red-800 text-red-500 text-xs tracking-widest hover:bg-red-600 hover:text-white transition-colors"
                      >
                        OPT OUT
                      </button>
                    )}
                  </div>
                </div>
                {l.pitch_text && (
                  <PitchEditor
                    lead={l}
                    onSaved={(updates) => setLeads((ls) => ls.map((x) => x.id === l.id ? { ...x, ...updates } : x))}
                    onSent={() => setLeads((ls) => ls.map((x) => x.id === l.id ? { ...x, status: "contacted", contacted_at: new Date().toISOString() } : x))}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-6 text-xs text-neutral-500">
          Delivery results moved to <a href="/hq/delivery" className="text-volt hover:underline">Operations → Delivery</a>.
        </div>

        <EmailCoverageSection />

        <JobDigestPanel />
      </div>
    </div>
  );
}

function JobDigestPanel() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => { api.get("/admin/job-digest/status").then(({ data }) => setStatus(data)).catch(() => {}); };
  useEffect(load, []);

  const toggle = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/job-digest/toggle");
      toast.success(data.enabled ? "Weekly digest ENABLED — sends Mondays 8 AM ET" : "Weekly digest paused");
      load();
    } catch (e) { toast.error("Toggle failed"); } finally { setBusy(false); }
  };

  const sendTest = async () => {
    const email = window.prompt("Send a test digest to which email?", "pd1@coachconnect.ai");
    if (!email) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/job-digest/run?test_email=${encodeURIComponent(email)}`);
      toast.success(`Test digest: sent ${data.sent} (${data.new_jobs} new jobs this week)`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Test failed"); } finally { setBusy(false); }
  };

  return (
    <div className="mt-6 border border-neutral-800 bg-appcard p-5" data-testid="job-digest-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs tracking-widest text-volt">WEEKLY JOB DIGEST · COACHES &amp; PLAYERS</div>
        <button
          data-testid="job-digest-toggle"
          disabled={busy || !status}
          onClick={toggle}
          className={`min-h-[40px] px-4 heading text-xs tracking-widest transition-colors ${
            status?.enabled ? "bg-blaze text-white hover:opacity-90" : "border border-volt text-volt hover:bg-volt hover:text-white"
          }`}
        >
          {status?.enabled ? "ENABLED — PAUSE" : "OFF — ENABLE"}
        </button>
      </div>
      <p className="text-xs text-neutral-500 mt-2">
        Every Monday 8 AM ET, all coach/player accounts get the week's new jobs — state-matched first. Every coach sees full program names and details (coaches are free). Opt-outs &amp; bounces are skipped automatically.
      </p>
      {status?.last_run && (
        <div className="text-[11px] text-neutral-500 mt-2">
          Last run: {new Date(status.last_run.at).toLocaleString()} · sent {status.last_run.sent}/{status.last_run.eligible}{status.last_run.test ? " (TEST)" : ""} · {status.last_run.new_jobs} new jobs
        </div>
      )}
      <button data-testid="job-digest-test" onClick={sendTest} disabled={busy} className="mt-3 text-xs tracking-widest text-neutral-400 hover:text-volt transition-colors">
        SEND TEST TO ONE EMAIL →
      </button>
    </div>
  );
}
